import KeenSlider from 'keen-slider';

if (!customElements.get('ia-slider')) {

  class IASlider extends HTMLElement {
    constructor() {
      super();
      this.slider = null;
      this.currentSlideElement = null;
      this.prevButton = null;
      this.nextButton = null;
      this.progressFill = null;
      this.bulletElements = null;
      this.autoplay = this.getAttribute('data-auto-play') === 'true';
      this.autoplayDelay = parseInt(this.getAttribute('data-auto-play-delay') || '2000', 10);
      this.autoplayTimeout = null;
      this.mouseOver = false;
      this.loop = this.dataset.loop === 'true';
      this.centeredDesktop = this.dataset.centeredDesktop === 'true';
      this.centeredMobile = this.dataset.centeredMobile === 'true';
      // console.log('IASlider constructor', this);
    }
  
    connectedCallback() {
      // Defer initialization if data-defer-init is set
      if (this.dataset.deferInit === 'true') {
        return;
      }
      this.initializeSlideshow();
    }
  
    disconnectedCallback() {
      this.clearAutoplayTimeout();
      if (this.slider) {
        this.slider.destroy();
      }
    }
  
    initializeSlideshow() {
      const carousel = this.querySelector('.ia-slider__slides');
      this.currentSlideElement = this.querySelector('.js-ia-slider-current');
      this.prevButton = this.querySelector('.js-ia-slider-prev');
      this.nextButton = this.querySelector('.js-ia-slider-next');
      this.progressFill = this.querySelector('.ia-slider__progress-fill');
      this.bulletElements = this.querySelectorAll('[data-slide-index], .ia-slider__bullet, .js-ia-slider-bullet');
  
      if (!carousel) return;

      const sliderConfig = {
        loop: this.loop,
        slides: {
          perView: this.style.getPropertyValue('--items-desktop'),
          spacing: this.style.getPropertyValue('--spacing-desktop').replace('px', ''),
          origin: this.centeredDesktop ? "center" : undefined,
        },
        breakpoints: {
          '(max-width: 1023px)': {
            slides: {
              perView: this.style.getPropertyValue('--items-mobile'),
              spacing: this.style.getPropertyValue('--spacing-mobile').replace('px', ''),
              origin: this.centeredMobile ? "center" : undefined,
            },
          },
          '(min-width: 1600px)': {
            slides: {
              spacing: this.style.getPropertyValue('--spacing-desktop').replace('px', ''),
              perView: this.style.getPropertyValue('--items-desktop-large') || this.style.getPropertyValue('--items-desktop'),
              origin: this.centeredDesktop ? "center" : undefined,
            },
          },
        },
        created: (slider) => {
          this.classList.add('is-initialized');
          const firstSlide = this.querySelector('.ia-slider__slide');
          if (firstSlide) {
            firstSlide.classList.add('is-active');
          }
          this.createProgressBar();
          this.initializeBullets();
          this.updateBullets();
          
          // Update progress after slider is created
          setTimeout(() => {
            this.updateProgress();
          }, 50);
        },
        slideChanged: (slider) => {
          this.updateProgress();
        }
      };

      // Enable loop for autoplay
      if (this.autoplay) {
        sliderConfig.loop = true;
      }

      const plugins = [this.createWheelPlugin()];
      if (this.autoplay) {
        plugins.push(this.createAutoplayPlugin());
      }
      this.slider = new KeenSlider(carousel, sliderConfig, plugins)
  
      if (this.prevButton) {
        this.prevButton.addEventListener('click', () => {
          this.slider.prev();
        });
      }
  
      if (this.nextButton) {
        this.nextButton.addEventListener('click', () => {
          this.slider.next();
        });
      }
  
      this.slider.on('slideChanged', (slide) => {
        const allSlides = this.querySelectorAll('.ia-slider__slide');
        allSlides.forEach(slide => slide.classList.remove('is-active'));
        
        const currentSlide = this.slider.track.details.rel;
        if (allSlides[currentSlide]) {
          allSlides[currentSlide].classList.add('is-active');
        }
        
        if (this.slider?.track?.details?.rel >= 0 && this.currentSlideElement) {
          this.currentSlideElement.textContent = this.slider.track.details.rel + 1;
        }
        
        this.updateBullets();
        this.updateProgress();
      });

    }
  
    initializeBullets() {
      if (!this.bulletElements || this.bulletElements.length === 0) {
        return;
      }

      this.bulletElements.forEach((bullet) => {
        const slideIndex = bullet.getAttribute('data-slide-index');
        if (slideIndex !== null) {
          bullet.addEventListener('click', () => {
            this.goToSlide(parseInt(slideIndex, 10));
          });
        }
      });
    }

    updateBullets() {
      if (!this.bulletElements || this.bulletElements.length === 0 || !this.slider) {
        return;
      }

      const currentSlide = this.slider.track.details.rel;
      
      this.bulletElements.forEach((bullet, index) => {
        const slideIndex = bullet.getAttribute('data-slide-index');
        const targetIndex = slideIndex !== null ? parseInt(slideIndex, 10) : index;
        
        if (targetIndex === currentSlide) {
          bullet.classList.add('is-active');
          bullet.setAttribute('aria-current', 'true');
        } else {
          bullet.classList.remove('is-active');
          bullet.setAttribute('aria-current', 'false');
        }
      });
    }
  
    createProgressBar() {
      if (this.dataset.showProgress !== 'true') return;

      if (this.querySelector('.ia-slider__progress')) return;

      const progressContainer = document.createElement('div');
      progressContainer.className = 'ia-slider__progress ' + this.dataset.colorSchemeUi;

      const progressBar = document.createElement('div');
      progressBar.className = 'ia-slider__progress-bar';

      const progressFill = document.createElement('div');
      progressFill.className = 'ia-slider__progress-fill';

      progressBar.appendChild(progressFill);
      progressContainer.appendChild(progressBar);
      this.appendChild(progressContainer);

      this.progressFill = progressFill;
    }

    updateProgress() {
      const d = this.slider?.track?.details;
      // details is null until keen-slider has measured slides (e.g. a carousel
      // that initialised empty while dynamic recommendations are still loading).
      if (!d) return;
      const totalSlides = d?.slides?.length || 0;
      const currentSlide = d.rel;
      const minIdx = typeof d.minIdx === 'number' ? d.minIdx : 0;
      const maxIdx = typeof d.maxIdx === 'number' ? d.maxIdx : (totalSlides - 1);
      const steps = Math.max(1, (maxIdx - minIdx + 1));
      const atStart = currentSlide <= minIdx;
      const atEnd   = currentSlide >= maxIdx;
      if (this.dataset.showProgress === 'true' && this.progressFill && this.slider) {
        
        // Only update if we have valid data
        if (totalSlides > 0) {
          // const progress = ((currentSlide + 1) / totalSlides) * 100;
          // this.progressFill.style.width = `${Math.min(progress, 100)}%`;
          
          // Progress by "page" (works with perView > 1)
          const page = currentSlide - minIdx + 1;
          const progress = Math.min(100, (page / steps) * 100);
          this.progressFill.style.width = `${progress}%`;
        }
      }

      if (this.prevButton) {
        this.prevButton.classList.toggle('ia-visibility-hidden', atStart);
      }

      if (this.nextButton) {
        this.nextButton.classList.toggle('ia-visibility-hidden', atEnd);
      }
    }
  
    next() {
      if (this.slider) {
        this.slider.next();
      }
    }
  
    prev() {
      if (this.slider) {
        this.slider.prev();
      }
    }
  
    goToSlide(index) {
      if (this.slider) {
        this.slider.moveToIdx(index);
      }
    }

    /** Sub-slide slack in Keen track units — tied to narrowest slide, not whole track (avoids overshooting layout). */
    wheelEdgeSlack(det) {
      if (!det?.slides?.length) return 0;
      const minIdx = typeof det.minIdx === 'number' ? det.minIdx : 0;
      const maxIdx =
        typeof det.maxIdx === 'number'
          ? det.maxIdx
          : Math.max(0, det.slides.length - 1);
      const idxSpan = Math.max(1, maxIdx - minIdx);
      const trackSpan = Math.max(0, det.max - det.min);
      if (trackSpan <= 0) return 0;
      const step = trackSpan / idxSpan;
      const slideSizes = det.slides
        .map((s) => s.size)
        .filter((n) => typeof n === 'number' && n > 0);
      const minSlide = slideSizes.length ? Math.min(...slideSizes) : step;
      const scale = parseFloat(this.dataset.wheelEdgeSlack || '1');
      const k = Number.isFinite(scale) && scale >= 0 ? scale : 1;
      return (
        Math.min(step * 0.08, minSlide * 0.08, trackSpan * 0.025) * k
      );
    }

    createWheelPlugin() {
      return (slider) => {
        const wheelPixels = (e) => {
          let dx = e.deltaX;
          let dy = e.deltaY;
          if (e.deltaMode === 1) {
            const line = 16;
            dx *= line;
            dy *= line;
          } else if (e.deltaMode === 2) {
            const el = slider.container;
            dx *= el.clientWidth;
            dy *= el.clientHeight;
          }
          return { dx, dy };
        };

        const onWheel = (e) => {
          const { dx, dy } = wheelPixels(e);
          const vertical = slider.options.vertical;
          let deltaPx = 0;
          if (vertical) {
            if (Math.abs(dy) > Math.abs(dx)) {
              deltaPx = dy;
            } else if (e.shiftKey && dx !== 0) {
              deltaPx = dx;
            }
          } else if (Math.abs(dx) > Math.abs(dy)) {
            deltaPx = dx;
          } else if (e.shiftKey && dy !== 0) {
            deltaPx = dy;
          }

          if (deltaPx === 0) return;

          const d = slider.track.details;
          if (!d) return;

          const size =
            slider.size ||
            slider.container?.clientWidth ||
            1;
          const rtl = slider.options.rtl ? -1 : 1;
          const speedOpt = slider.options.dragSpeed || 1;
          const dragSpeed =
            typeof speedOpt === 'function'
              ? speedOpt
              : (v) => v * speedOpt;
          const deltaTrack = (dragSpeed(deltaPx) / size) * rtl;

          const slack = this.loop ? 0 : this.wheelEdgeSlack(d);
          const minBound = d.min - slack;
          const maxBound = d.max + slack;
          const nextPos = d.position + deltaTrack;
          const eps = 1e-7;

          if (!this.loop) {
            if (deltaTrack < 0 && nextPos < minBound - eps) {
              return;
            }
            if (deltaTrack > 0 && nextPos > maxBound + eps) {
              return;
            }
          }

          e.preventDefault();
          slider.track.add(deltaTrack);
          const d2 = slider.track.details;
          if (!d2 || this.loop) return;
          const s2 = this.wheelEdgeSlack(d2);
          const lo = d2.min - s2;
          const hi = d2.max + s2;
          const p = d2.position;
          if (p < lo || p > hi) {
            slider.track.to(Math.min(Math.max(p, lo), hi));
          }
        };

        slider.on('created', () => {
          slider.container.addEventListener('wheel', onWheel, { passive: false });
        });

        slider.on('destroyed', () => {
          slider.container.removeEventListener('wheel', onWheel);
        });
      };
    }

    createAutoplayPlugin() {
      return (slider) => {
        const clearNextTimeout = () => {
          this.clearAutoplayTimeout();
        };

        const nextTimeout = () => {
          clearNextTimeout();
          if (this.mouseOver) return;
          this.autoplayTimeout = setTimeout(() => {
            if (this.slider) {
              this.slider.next();
            }
          }, this.autoplayDelay);
        };

        slider.on('created', () => {
          this.addEventListener('mouseenter', () => {
            this.mouseOver = true;
            clearNextTimeout();
          });

          this.addEventListener('mouseleave', () => {
            this.mouseOver = false;
            nextTimeout();
          });

          nextTimeout();
        });

        slider.on('dragStarted', clearNextTimeout);
        slider.on('animationEnded', nextTimeout);
        slider.on('updated', nextTimeout);
      };
    }

    clearAutoplayTimeout() {
      if (this.autoplayTimeout) {
        clearTimeout(this.autoplayTimeout);
        this.autoplayTimeout = null;
      }
    }

    destroy() {
      this.clearAutoplayTimeout();
      if (this.slider) {
        this.slider.destroy();
        this.slider = null;
      }
    }
  }
  
  
  customElements.define('ia-slider', IASlider); 
}